import { orderBy } from 'lodash-es'

import { isHazardous, showApiError, useMutation } from '@fv/client-core'
import type { FullShipment, ShipmentLocation } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'
import type { LocationType, StopType } from '@fv/models'

import { billToMapper } from '@/features/billTo/billToFuncs'
import { useCreateQuoteRequest } from '@/features/quote-request/useCreateQuoteRequest'
import type { ApiQuoteRequestLocationsDTO } from '@/types/api-dto'
import { defaultStopDate } from '@/utils/shipmentFuncs'

function reverseStopType(stopType: StopType): StopType {
  if (stopType === 'both') {
    return stopType
  }

  return stopType === 'pickup' ? 'delivery' : 'pickup'
}

function flipLocationType(locType: LocationType): LocationType {
  if (locType === 'stop') {
    return 'stop'
  }

  return locType === 'origin' ? 'destination' : 'origin'
}

export function reverseLocations(
  locations: ShipmentLocation[],
): ApiQuoteRequestLocationsDTO {
  const sorted = orderBy(locations, ['sequence'], ['desc'])

  return sorted.map<ShipmentLocation>((loc, i) => {
    const type = flipLocationType(loc.type)
    const stopDate = defaultStopDate(type)

    return {
      ...loc,
      sequence: i,
      stopDate: stopDate ? fvDateUtc(stopDate).format('YYYY-MM-DD') : null,
      stopType: reverseStopType(loc.stopType),
      type,
    }
  })
}

export function useCreateReturnShipment() {
  const createQuoteRequest = useCreateQuoteRequest()

  return useMutation(async (load: FullShipment) => {
    const billTo = billToMapper.loadToDTO(load.billTo)

    return await createQuoteRequest.mutateAsync(
      {
        equipment: [
          {
            ...load.equipment,
            loadId: load.loadId,
            accessorials: load.equipment.accessorials ?? [],
            alternateTypes: load.equipment.alternateTypes ?? [],
            items: (load.items ?? []).map(i => ({
              ...i,
              ...(i.contains && {
                contains: i.contains.map(c => ({
                  ...c,
                  ...(!c.description && { description: i.description }),
                })),
              }),
            })),
            ...(billTo && { billTo }),
            customsBroker: load.customsBroker,
            ...(isHazardous(load) && {
              emergencyContact: load.emergencyContact,
            }),
          },
        ],
        isLiveLoad: true,
        locations: reverseLocations(load.locations),
        orderNumber: '',
        workflow: load.workflow,
        ...(billTo && { billTo }),
        isFreightCollect: load.isFreightCollect ?? false,
      },
      {
        onError: err => {
          showApiError('Unable to create return shipment', err)
        },
      },
    )
  })
}
